'use client'
import s from './Hero.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import LazyImage from '@/components/common/LazyImage'
import { Image } from '@/types/common/ImageData'
import { WebContext } from '@/context/webContext'
import CreditsOnTopComponent from '../credits'
import LazyVideo from '@/components/common/LazyVideo'
import CreditsOnTopMobileComponent from '../creditsMobile'

interface ImageProp {
    image: Image | null;
}

interface Hero {
    image: ImageProp | null;
    imageMobile: ImageProp | null;
    mediaType: string;
    video: {
        videoUrl: string | null;
        videoMobileUrl: string | null;
    };
    headerColor: string | null;
}

interface IntroductionProps {
    hero: Hero;
    title: string;
    location: string | null;
    creditsOnTop: any;
    bua: string | null;
    year: string | null;
    region: string | null;
    typologies: any;
    status: string | null;
    client: string | null;
}

export default function HeroProjectComponent({
    hero,
    title,
    location,
    creditsOnTop,
    bua,
    typologies,
    year,
    region,
    status,
    client
}: IntroductionProps) {

    const {
        setColorHeader,
        setBackgroundHeader,
        setOpenOverview,
        setPastColorHeader,
        setPastBackgroundHeader,
        colorHeader,
        backgroundHeader
    } = useContext(WebContext)

    const heroRef = useRef<HTMLDivElement | null>(null);

    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es mobile o desktop
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    useEffect(() => {
        setColorHeader(hero.headerColor);
        setBackgroundHeader(false);
    }, []);

    useEffect(() => {
        setColorHeader(hero.headerColor);
        setBackgroundHeader(false);

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // El Hero está visible
                    setColorHeader(hero.headerColor);
                    setBackgroundHeader(false);
                } else {
                    // El Hero ya no está visible
                    setBackgroundHeader(true);
                    setColorHeader('black');
                }
            },
            { threshold: 0.1 } // Umbral para considerar el Hero visible
        );

        if (heroRef.current) {
            observer.observe(heroRef.current);
        }

        return () => {
            if (heroRef.current) {
                observer.unobserve(heroRef.current);
            }
        };
    }, [hero, setColorHeader, setBackgroundHeader]);

    // Función para manejar el clic en el botón "OVERVIEW"
    const handleOverviewClick = () => {
        setOpenOverview(true); // Abre la galería en modo "overview"
        setPastColorHeader(colorHeader);
        setPastBackgroundHeader(backgroundHeader);
        setTimeout(() => {
            setColorHeader('black'); // Cambia el color del texto del header a negro
            setBackgroundHeader(true); // Cambia el fondo del header a blanco
        }, 100);

    };

    return (
        <>
            <section className={s.heroComponent}>

                {<div ref={heroRef} className={s.hero}>
                    <div className={s.heroMedia}>
                        {hero.mediaType === 'video' ? (
                            // VIDEO BLOCK
                            isMobile && hero.video?.videoMobileUrl ? (
                                <LazyVideo
                                    src={hero.video.videoMobileUrl}
                                    autoplay={true}
                                    controls={false}
                                    defaultInView={true}
                                    alt={title || ""}
                                />
                            ) : hero.video?.videoUrl ? (
                                <LazyVideo
                                    src={hero.video.videoUrl}
                                    autoplay={true}
                                    controls={false}
                                    defaultInView={true}
                                    alt={title || ""}
                                />
                            ) : null
                        ) : hero.mediaType === 'image' ? (
                            // IMAGE BLOCK
                            isMobile && hero.imageMobile?.image?.imageUrl ? (
                                <LazyImage
                                    src={hero.imageMobile.image.imageUrl}
                                    width={0}
                                    height={0}
                                    blurDataURL={hero.imageMobile.image.ref}
                                    fill
                                    alt={hero.imageMobile.image.attribution || ""}
                                    caption={hero.imageMobile.image.caption || null}
                                    filename={hero.imageMobile.image.filename || ""}
                                />
                            ) : hero.image?.image?.imageUrl ? (
                                <LazyImage
                                    src={hero.image.image.imageUrl}
                                    width={0}
                                    height={0}
                                    blurDataURL={hero.image.image.ref}
                                    fill
                                    alt={hero.image.image.attribution || ""}
                                    caption={hero.image.image.caption || null}
                                    filename={hero.image.image.filename || ""}
                                />
                            ) : null
                        ) : null}
                    </div>
                    <div className={s.heroContent} style={{ color: hero.headerColor ? hero.headerColor : 'white' }}>
                        <div className={s.heroCredits}>
                            <CreditsOnTopComponent
                                credits={creditsOnTop}
                                year={year}
                                status={status}
                                client={client}
                                bua={bua}
                                location={location}
                                color={hero.headerColor ? hero.headerColor : 'white'}
                            />
                        </div>
                        <div className={s.heroText}>
                            <h1>{title}{location && <><br></br>{location}</>}</h1>
                        </div>
                    </div>
                </div>
                }
                <div className={s.overviewButton} onClick={handleOverviewClick}>
                    <p>OVERVIEW</p>
                </div>
            </section>
            <div className={s.creditsOnTopMobile}>
                <CreditsOnTopMobileComponent
                    credits={creditsOnTop}
                    year={year}
                    status={status}
                    client={client}
                    bua={bua}
                    location={location}
                />
            </div>
            <div className={s.projectTitle}>
                <h2>{title}</h2>
            </div>

        </>
    )
}
